import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const AuthPage = () => {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        if (form.phone.length !== 10) { setError('Phone must be 10 digits'); setLoading(false); return; }
        if (form.password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
        await register(form.name, form.email, form.phone, form.password);
      }
      navigate('/account');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="auth-page">
      <SEO title={mode === 'login' ? 'Login' : 'Sign Up'} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl border border-[#E8DFD3] shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#2C1810] px-6 py-6 text-center">
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {mode === 'login' ? 'Welcome Back' : 'Join Nistala\'s'}
            </h1>
            <p className="text-sm text-white/60 mt-1">
              {mode === 'login' ? 'Access your membership benefits' : 'Create your account & start earning rewards'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#E8DFD3]">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className={`flex-1 py-3 text-sm font-semibold cursor-pointer transition-colors ${mode === 'login' ? 'text-[#5C7A3A] border-b-2 border-[#5C7A3A]' : 'text-[#7a6555]'}`}
              data-testid="auth-login-tab"
            >Login</button>
            <button
              onClick={() => { setMode('register'); setError(''); }}
              className={`flex-1 py-3 text-sm font-semibold cursor-pointer transition-colors ${mode === 'register' ? 'text-[#5C7A3A] border-b-2 border-[#5C7A3A]' : 'text-[#7a6555]'}`}
              data-testid="auth-register-tab"
            >Sign Up</button>
          </div>

          {/* Form */}
          <div className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-xl mb-4" data-testid="auth-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5"><User size={13} className="text-[#7a6555]" /> Full Name</label>
                  <Input data-testid="auth-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" required />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5"><Mail size={13} className="text-[#7a6555]" /> Email</label>
                <Input data-testid="auth-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" required />
              </div>
              {mode === 'register' && (
                <div>
                  <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5"><Phone size={13} className="text-[#7a6555]" /> Phone</label>
                  <Input data-testid="auth-phone" type="tel" value={form.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(f => ({ ...f, phone: v })); }} placeholder="10-digit mobile" maxLength={10} required />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5"><Lock size={13} className="text-[#7a6555]" /> Password</label>
                <div className="relative">
                  <Input data-testid="auth-password" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={mode === 'register' ? 'Min 6 characters' : 'Enter password'} required />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a6555] hover:text-[#2C1810] cursor-pointer">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-5 cursor-pointer" data-testid="auth-submit-btn">
                {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Please wait...</> : <>{mode === 'login' ? 'Login' : 'Create Account'} <ArrowRight size={14} className="ml-2" /></>}
              </Button>
            </form>

            {mode === 'login' && (
              <p className="text-center text-xs text-[#7a6555] mt-4">
                Don't have an account? <button onClick={() => setMode('register')} className="text-[#5C7A3A] font-semibold cursor-pointer hover:underline">Sign up free</button>
              </p>
            )}
            {mode === 'register' && (
              <p className="text-center text-xs text-[#7a6555] mt-4">
                Already a member? <button onClick={() => setMode('login')} className="text-[#5C7A3A] font-semibold cursor-pointer hover:underline">Login</button>
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
